import React, { createRef, useEffect, useState } from "react";

import Alert from "@mui/lab/Alert";
import AlertTitle from "@mui/lab/AlertTitle";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import DialogContentText from "@mui/material/DialogContentText";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import { ErrorMessage, Form, Formik, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import PayNowSchema, { IPayNowState, PaymentAmountOption, PaymentMethod } from "./PayNowSchema";
import Balances from "../../components/Billing/Balances";
import ApiErrorMessage from "../../components/Common/ApiErrorMessage";
import { ButtonPrimary } from "../../components/Common/Buttons/Buttons";
import ConfirmDialog from "../../components/Common/Dialogs/ConfirmDialog";
import BankAccount, { IBankAccountProps, IBankAccoutRef } from "../../components/Payment/BankAccount";
import CreditCard, { ICardRef, ICreditCardProps } from "../../components/Payment/CreditCard";
import { CardLayout } from "../../layouts/Layouts";
import { getCurrentAccountBalance } from "../../store/modules/accountBalance/accountBalanceActions";
import { resetAccountBalance } from "../../store/modules/accountBalance/accountBalanceStateActions";
import { getCryptoTokens } from "../../store/modules/cryptoToken/cryptoTokenActions";
import { loadPastStatementInformation } from "../../store/modules/pastCharges/pastChargesActions";
import { resetStatementInformation } from "../../store/modules/pastCharges/pastChargesStateActions";
import {
  makeOneOffBankPayment,
  makeOneOffCardPayment,
  makeRegularPayment,
} from "../../store/modules/payment/paymentActions";
import { getCurrentPaymentMethod } from "../../store/modules/paymentMethod/paymentMethodActions";
import { RootState } from "../../store/store";
import { AccountStatement, PaymentType, StatementInformation } from "../../types/types";
import { bankMerchantTransactionFee, ccMerchantTransactionFee } from "../../utils/constants";
import { getAvailableToken } from "../../utils/cryptoTokenHelper";
import { formatCurrency } from "../../utils/helpers";
import { useIsMaguireEnabled } from "../../utils/maguireHelper";

export default function PayNow() {
  const isMaguireEnabled = useIsMaguireEnabled();
  const balance = useSelector((state: RootState) => state.balance.balanceState.payload);
  const currentPaymentMethod = useSelector((state: RootState) => state.paymentMethod.payload);
  const customer_account_id = useSelector((state: RootState) => state.auth.user?.profile.customer_account_id);
  const isProcessing = useSelector((state: RootState) => state.payment.isProcessing);
  const creditCardSurcharge = useSelector((state: RootState) => state.appConfigs.creditCardSurcharge);
  const paymentFees = useSelector((state: RootState) => state.commonData.data?.paymentFees);
  const customer_contact_id = useSelector((state: RootState) => state.auth.user?.profile.customer_contact_id);
  const cryptoTokenState = useSelector((state: RootState) => state.cryptoToken);
  const cryptoTokenIsLoading = useSelector((state: RootState) => state.cryptoToken.isLoading);
  const cryptoTokenIsLoaded = useSelector((state: RootState) => state.cryptoToken.isLoaded);
  const cryptoToken = getAvailableToken(cryptoTokenState);

  const cardRef = createRef<ICardRef>();
  const bankRef = createRef<IBankAccoutRef>();

  const dispatch = useDispatch();
  const isError = useSelector((state: RootState) => state.payment.isError);
  const isSuccess = useSelector((state: RootState) => state.payment.isSuccess);
  const [open, setOpen] = React.useState(false);
  const [amountBlockedMessage, setAmountBlockedMessage] = useState<string | null>(null);

  const currentBalance = useSelector((state: RootState) => state.balance.currentBalanceState.accountBalance);
  const statementInformation = useSelector(
    (state: RootState) => state.pastCharges.statementInformationState.statementInformation
  );
  const maguireStatement = statementInformation?.[0] ?? null;

  const handleConfirmOpen = () => {
    setOpen(true);
  };

  const handleConfirmClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!currentPaymentMethod) {
      getCurrentPaymentMethod(dispatch, customer_account_id ?? 0);
    }
  }, [currentPaymentMethod, customer_account_id]);

  useEffect(() => {
    if (!cryptoTokenIsLoaded && !cryptoTokenIsLoading) {
      getCryptoTokens(dispatch);
    }
  }, [cryptoTokenIsLoading, cryptoTokenIsLoaded]);

  useEffect(() => {
    if (isError || isSuccess) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [isError, isSuccess]);

  function getAmount(values: IPayNowState, includeSurcharge: boolean): number {
    // Must use the same balance source as the PaymentAmount radio labels.
    let amount =
      values.amountOption === "Current"
        ? isMaguireEnabled
          ? currentBalance ?? 0
          : balance?.balance ?? 0
        : values.amountOption === "Statment"
        ? isMaguireEnabled
          ? maguireStatement?.currentDueAmount ?? 0
          : balance?.statementBalance ?? 0
        : values.amount;
    let isSurcharge =
      values.method === "CreditCard" ||
      (values.method === "Default" && currentPaymentMethod?.paymentType.toLowerCase() === "creditcard");
    let isBankAccTransactionCharge =
      values.method === "BankAccount" ||
      (values.method === "Default" && currentPaymentMethod?.paymentType.toLowerCase() === "bankaccount");
    if (isSurcharge && includeSurcharge) {
      if (!isMaguireEnabled && creditCardSurcharge) {
        let surcharge = 1 + creditCardSurcharge / 100;
        amount *= surcharge;
        amount += ccMerchantTransactionFee;
      }
      if (isMaguireEnabled && paymentFees?.creditCardTransactionFee && paymentFees?.creditCardSurcharge) {
        let surcharge = 1 + paymentFees?.creditCardSurcharge / 100;
        amount *= surcharge;

        amount += paymentFees?.creditCardTransactionFee;
      }
    }
    if (isBankAccTransactionCharge && includeSurcharge && isMaguireEnabled) {
      if (paymentFees?.bankTransferTransactionFee) {
        amount += paymentFees?.bankTransferTransactionFee;
      }
    } else if (isBankAccTransactionCharge && includeSurcharge && !isMaguireEnabled) {
      amount += bankMerchantTransactionFee;
    }

    return amount;
  }

  function getPaymentMethodDescription() {
    return `${currentPaymentMethod?.paymentTypeName} ending with ....${currentPaymentMethod?.masked}`;
  }
  function isCreditCard(values: IPayNowState): boolean {
    switch (values.method) {
      case "Default":
        return currentPaymentMethod?.paymentType === PaymentType.CreditCard;
      case "CreditCard":
        return true;

      default:
        return false;
    }
  }
  function isBankAccount(values: IPayNowState): boolean {
    switch (values.method) {
      case "Default":
        return currentPaymentMethod?.paymentType === PaymentType.BankAccount;
      case "BankAccount":
        return true;

      default:
        return false;
    }
  }
  function getPaymentMethod(values: IPayNowState): string {
    switch (values.method) {
      case "Default":
        return getPaymentMethodDescription();
      case "CreditCard":
        return `Credit Card ending with ...${
          values.card.cardNumber && values.card.cardNumber.substring(values.card.cardNumber.length - 4)
        }`;
      case "BankAccount":
        return `Bank Account ending with ...${
          values.account.accountNumber &&
          values.account.accountNumber.toString().substring(values.account.accountNumber.toString().length - 4)
        }`;
      default:
        return "";
    }
  }

  function processPayment(values: IPayNowState): void {
    let amount: number = getAmount(values, false);
    switch (values.method) {
      case "BankAccount":
        values.account.bsb &&
          values.account.accountNumber &&
          makeOneOffBankPayment(dispatch, customer_account_id ?? 0, customer_contact_id ?? 0, amount, {
            accountName: values.account.accountName,
            accountNumber: values.account.accountNumber.toString(),
            bsb: values.account.bsb.toString(),
          });
        break;

      case "CreditCard":
        if (cryptoToken !== null) {
          makeOneOffCardPayment(dispatch, cryptoToken, customer_account_id ?? 0, customer_contact_id ?? 0, amount, {
            month: parseInt(values.card.expiry.substr(0, 2)),
            year: parseInt(values.card.expiry.substring(3, 7)),
            name: values.card.fullName,
            number: values.card.cardNumber,
            cvc: values.card.cvcNumber,
          });
        }
        break;

      case "Default":
        makeRegularPayment(dispatch, customer_account_id ?? 0, customer_contact_id ?? 0, amount);
        break;
    }
  }
  useEffect(() => {
    const loadData = async () => {
      if (isMaguireEnabled && isSuccess && customer_account_id !== undefined) {
        dispatch(resetStatementInformation());
        dispatch(resetAccountBalance());
        loadPastStatementInformation(dispatch, customer_account_id, 5, 1);
        getCurrentAccountBalance(dispatch, customer_account_id);
      }
    };
    loadData();
  }, [isSuccess, dispatch, customer_account_id]);

  const useStyles = makeStyles()((theme) => {
    return {
      root: {
        display: "flex",
        flexWrap: "wrap",
      },
      control: {
        padding: theme.spacing(2),
      },
      h1: {
        marginTop: 0,
      },
      backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "white",
      },
      textField: {
        marginBottom: theme.spacing(2),
      },
    };
  });
  const { classes } = useStyles();

  function getDefaultPaymentAmount(): PaymentAmountOption {
    const statementAmount = isMaguireEnabled ? maguireStatement?.currentDueAmount : balance?.statementBalance;
    const currentAmount = isMaguireEnabled ? currentBalance : balance?.balance;
    if (statementAmount && statementAmount > 0) return "Statment";
    if (currentAmount && currentAmount > 0) return "Current";
    return "Custom";
  }

  return (
    <CardLayout>
      <CardLayout.Header bgColor="white">
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={2}>
            <h1 className={`h2 ${classes.h1}`}>Pay Now</h1>
          </Grid>
          <Grid item xs={8}>
            <Balances />
          </Grid>
        </Grid>
      </CardLayout.Header>
      <CardLayout.Body>
        <Formik
          validationSchema={PayNowSchema}
          initialValues={{
            amount: 0,
            method: currentPaymentMethod ? ("Default" as PaymentMethod) : ("CreditCard" as PaymentMethod),
            amountOption: getDefaultPaymentAmount(),
            card: {
              cardType: "Mastercard",
              cardNumber: "",
              fullName: "",
              expiry: "",
              cvcNumber: "",
              path: "card.",
            } as ICreditCardProps,
            account: {
              path: "account.",
              isOneOffPayment: true,
              bsb: undefined,
              accountName: "",
              accountNumber: undefined,
            } as IBankAccountProps,
          }}
          onSubmit={(values) => {
            var isValid = true;
            if (cardRef != null && cardRef.current) {
              isValid = cardRef.current.validate();
            }
            if (bankRef != null && bankRef.current) {
              isValid = bankRef.current.validate();
            }

            // Current/Statment amounts come from live balance data that can be stale or not yet loaded - block those instead of silently charging <= 0.
            if (values.amountOption !== "Custom" && getAmount(values, false) <= 0) {
              setAmountBlockedMessage(
                "This balance isn't available to pay right now. Please refresh the page and try again, or use a Custom Amount."
              );
              return;
            }

            if (isValid) {
              setAmountBlockedMessage(null);
              handleConfirmOpen();
            }
          }}
        >
          {({ handleChange, setSubmitting, isSubmitting, values, touched, errors }) => (
            <Form autoComplete="off">
              <ConfirmDialog
                title="Confirm Payment"
                open={open}
                onClose={handleConfirmClose}
                onConfirm={() => {
                  setTimeout(() => {
                    setSubmitting(false);
                    processPayment(values);
                  }, 500);
                  handleConfirmClose();
                }}
              >
                <DialogContentText>
                  {isCreditCard(values)
                    ? getMerchantFeeChargeNotification(
                        paymentFees?.creditCardSurcharge,
                        paymentFees?.creditCardTransactionFee,
                        isMaguireEnabled
                      )
                    : null}
                  {isMaguireEnabled && isBankAccount(values)
                    ? getBankTransferChargeNotification(paymentFees?.bankTransferTransactionFee)
                    : null}
                  {!isMaguireEnabled &&
                    isBankAccount(values) &&
                    getBankTransferChargeNotification(bankMerchantTransactionFee)}
                  You are about to make a payment. Do you wish to continue?
                  <Grid item xs={12} className={classes.control}>
                    <strong>TOTAL</strong> {formatCurrency(getAmount(values, true))}
                  </Grid>
                  <Grid item xs={12} className={classes.control}>
                    <strong> PAYMENT METHOD</strong> {getPaymentMethod(values)}
                  </Grid>
                </DialogContentText>
              </ConfirmDialog>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Divider></Divider>
                  <NotificationMessage />
                </Grid>
                <Grid item xs={12}>
                  <PaymentAmount
                    balance={balance}
                    currentBalance={currentBalance}
                    maguireStatement={maguireStatement}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset">
                    <Box paddingBottom={3} paddingTop={3}>
                      <strong> Payment Method</strong>
                    </Box>
                    <RadioGroup
                      aria-label="method"
                      id="method"
                      name="method"
                      value={values.method}
                      onChange={handleChange}
                    >
                      {currentPaymentMethod && (
                        <FormControlLabel value="Default" control={<Radio />} label={getPaymentMethodDescription()} />
                      )}
                      <FormControlLabel value="CreditCard" control={<Radio />} label="Credit Card" />
                      <FormControlLabel value="BankAccount" control={<Radio />} label="Bank Account" />
                    </RadioGroup>
                  </FormControl>
                  <Box paddingTop={3} />
                  {values.method === "CreditCard" && <CreditCard ref={cardRef} {...values.card} />}
                  {values.method === "BankAccount" && <BankAccount ref={bankRef} {...values.account} />}
                </Grid>
                {amountBlockedMessage && (
                  <Grid item xs={12}>
                    <Alert severity="warning">{amountBlockedMessage}</Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <ButtonPrimary type="submit" disabled={isProcessing}>
                    <strong>Pay Now</strong>
                  </ButtonPrimary>
                </Grid>
              </Grid>
              <Backdrop className={classes.backdrop} open={isProcessing}>
                <CircularProgress color="primary" />
              </Backdrop>
            </Form>
          )}
        </Formik>
      </CardLayout.Body>
    </CardLayout>
  );
}
interface IAmountProps {
  balance: AccountStatement | null;
  currentBalance: number | null;
  maguireStatement: StatementInformation | null;
}
export function getMerchantFeeChargeNotification(
  creditCardSurchargeNew?: number,
  creditCardTransactionFee?: number,
  isMaguireEnabled?: boolean
) {
  const surcharge = creditCardSurchargeNew ?? 0;
  const transactionFee = isMaguireEnabled ? creditCardTransactionFee ?? 0 : ccMerchantTransactionFee;

  if (surcharge <= 0 && transactionFee <= 0) {
    return null;
  }

  const feeText =
    surcharge > 0 && transactionFee > 0
      ? `${surcharge}% plus $${transactionFee}`
      : surcharge > 0
      ? `${surcharge}%`
      : `$${transactionFee}`;

  return (
    <Box paddingBottom={3}>
      <Alert severity="warning">
        <AlertTitle>
          {isMaguireEnabled
            ? "Payments made using credit or debit cards are subject to the following surcharges:"
            : "All payments by credit or debit card will incur the following surcharges:"}
        </AlertTitle>
        MasterCard (including Debit MasterCard): {feeText}
        <br />
        VISA (including VISA Debit Card): {feeText}
      </Alert>
    </Box>
  );
}
export function getBankTransferChargeNotification(bankTransferTransactionFee?: number) {
  if (!bankTransferTransactionFee || bankTransferTransactionFee <= 0) {
    return null;
  }
  return (
    <Box paddingBottom={3}>
      <Alert severity="warning">
        <AlertTitle>
          All Payments via Bank Accounts will incur a transaction fee of ${bankTransferTransactionFee}
        </AlertTitle>
      </Alert>
    </Box>
  );
}

function PaymentAmount({ balance, currentBalance, maguireStatement }: IAmountProps) {
  const { values, handleChange } = useFormikContext<IPayNowState>();
  const isMaguireEnabled = useIsMaguireEnabled();
  return (
    <FormControl component="fieldset">
      <Box paddingBottom={3} paddingTop={3}>
        <strong>Payment Amount</strong>
      </Box>
      <RadioGroup aria-label="amountOption" name="amountOption" value={values.amountOption} onChange={handleChange}>
        {!isMaguireEnabled && balance && balance.statementBalance !== undefined && balance.statementBalance > 0 && (
          <FormControlLabel
            value="Statment"
            control={<Radio />}
            label={`Balance at ${balance.statementDate} ${formatCurrency(balance.statementBalance)}`}
          />
        )}
        {isMaguireEnabled &&
          maguireStatement != null &&
          maguireStatement?.currentDueAmount !== undefined &&
          maguireStatement?.currentDueAmount > 0 && (
            <FormControlLabel
              value="Statment"
              control={<Radio />}
              label={`Balance at ${maguireStatement.dueDate} ${formatCurrency(maguireStatement.currentDueAmount)}`}
            />
          )}

        {!isMaguireEnabled && balance && balance.balance !== undefined && balance.balance > 0 && (
          <FormControlLabel
            value="Current"
            control={<Radio />}
            label={`Current Balance ${formatCurrency(balance.balance)}`}
          />
        )}
        {isMaguireEnabled && currentBalance != null && currentBalance !== undefined && currentBalance > 0 && (
          <FormControlLabel
            value="Current"
            control={<Radio />}
            label={`Current Balance ${formatCurrency(currentBalance ?? 0)}`}
          />
        )}
        <FormControlLabel value="Custom" control={<Radio />} label="Custom Amount" />
      </RadioGroup>
      {values.amountOption === "Custom" && (
        <>
          <TextField
            id="amount"
            type="number"
            onChange={handleChange}
            placeholder="Enter amount"
            value={values.amount}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          ></TextField>
          <ErrorMessage name={`amount`}>{(msg) => <Alert severity="warning">{msg}</Alert>}</ErrorMessage>
        </>
      )}
    </FormControl>
  );
}

const NotificationMessage = (props: any) => {
  const [successLocal, setSuccessLocal] = React.useState(false);
  const [msgLocal, setMsgLocal] = React.useState("");
  const [correlationIdLocal, setCorrelationIdLocal] = React.useState<string | undefined>(undefined);
  const [failed, setFailed] = React.useState(false);
  const isSuccess = useSelector((state: RootState) => state.payment.isSuccess);
  const successMessage = useSelector((state: RootState) => state.payment.title);
  const isError = useSelector((state: RootState) => state.payment.isError);
  const paymentError = useSelector((state: RootState) => state.payment.error);
  const { resetForm } = useFormikContext();

  useEffect(() => {
    if (isSuccess) {
      setMsgLocal(successMessage ?? "");
      setSuccessLocal(isSuccess);
      resetForm({});
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      setMsgLocal(paymentError?.message ?? "");
      setCorrelationIdLocal(paymentError?.correlationId);
      setFailed(isError);
    }
  }, [isError]);

  let msg = failed ? (
    <Alert severity="error">
      <ApiErrorMessage message={msgLocal} correlationId={correlationIdLocal} />
    </Alert>
  ) : successLocal ? (
    <Alert severity="success">{msgLocal}</Alert>
  ) : (
    <div></div>
  );

  return <> {msg}</>;
};
